import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { APP_CONFIG, IAppConfig } from '../../../app.config';
import { AutoCompleteSelectionItem, IAutoCompleteService } from '../../../core/component/autocomplete-remote/autocomplete-remote.component';
import { MessageService } from '../../../core/service/message.service';

export type PropertyBaseSearchRentalObjectCategory =
  | 'property'
  | 'dwelling'
  | 'parking'
  | 'otherRentalObject'
  | 'premises'
  | 'building';

export function PropertyBaseSearchRentalObjectCategoryToString(category: PropertyBaseSearchRentalObjectCategory): string {
  switch (category) {
    case 'dwelling':
      return 'CONST.RENTAL_OBJECT_CATEGORY_DWELLING';
    case 'otherRentalObject':
      return 'CONST.RENTAL_OBJECT_CATEGORY_OTHER';
    case 'parking':
      return 'CONST.RENTAL_OBJECT_CATEGORY_PARKING';
    case 'premises':
      return 'CONST.RENTAL_OBJECT_CATEGORY_PREMISES';
    case 'property':
      return 'COMMON.PROPERTY';
    case 'building':
      return 'COMMON.BUILDING';

    default:
      throw new Error('PropertyBaseSearchRentalObjectCategory: non-existing category');
  }
}

export class PropertyBaseSearchResultItem<TData = any> extends AutoCompleteSelectionItem<TData> {
  constructor(context: any) {
    super(context.name, context.value, context.data, context.shortName);
    this.category = context.category;
  }
  category: PropertyBaseSearchRentalObjectCategory;
}

export interface ICategorizedAutoCompleteService extends IAutoCompleteService {
  get categories(): string[];
}

@Injectable()
export class PropertyBaseSearchService implements IAutoCompleteService {
  constructor(rentalObjectSearch: RentalObjectSearchService, propertySearchService: PropertySearchService) {
    this.searchProviders = [rentalObjectSearch, propertySearchService];
  }
  searchProviders: ICategorizedAutoCompleteService[];

  getSelectionItem(
    id: string,
    state: { selectedItem: PropertyBaseSearchResultItem; options: any }
  ): Observable<PropertyBaseSearchResultItem> {
    // The detailed view is the same as the list view. No need to fetch again.
    if (state.selectedItem && state.selectedItem.value === id) return of(state.selectedItem);

    return forkJoin(this.getProviders(state.options).map(p => p.getSelectionItem(id, state))).pipe(
      map(results => results.find(res => res) as PropertyBaseSearchResultItem)
    );
  }

  private getProviders(options: any): ICategorizedAutoCompleteService[] {
    let categories =
      options && options.categories
        ? options.categories
        : ['property', 'dwelling', 'parking', 'otherRentalObject', 'premises', 'building', 'tenant'];

    let providers: ICategorizedAutoCompleteService[] = [];
    for (let provider of this.searchProviders) {
      if (provider.categories.find(c => categories.find(cat => cat === c))) {
        providers.push(provider);
      }
    }
    return providers;
  }

  getSelection(filter: string, options: any, page?: number): Observable<PropertyBaseSearchResultItem[]> {
    let queryArgs = {
      filter: filter,
      maxItems: 20,
    };
    if (options) queryArgs['options'] = options;
    if (page && page > 0) queryArgs['page'] = page;

    return forkJoin(this.getProviders(options).map(p => p.getSelection(filter, options, page))).pipe(
      map(results => {
        let items: PropertyBaseSearchResultItem[] = [];
        for (let resultArray of results) {
          items = items.concat(resultArray.map(r => r as PropertyBaseSearchResultItem));
        }
        return items;
      })
    );
  }

  getSelectionPaged(filter: string, options: any, page?: number): Observable<PropertyBaseSearchResultItem[]> {
    return forkJoin(this.getProviders(options).map(p => p.getSelection(filter, options))).pipe(
      map(results => {
        let items: PropertyBaseSearchResultItem[] = [];
        for (let resultArray of results) {
          items = items.concat(resultArray.map(r => r as PropertyBaseSearchResultItem));
        }
        return items;
      })
    );
  }
}

export abstract class PropertyBaseSearchServiceBase implements ICategorizedAutoCompleteService {
  constructor(
    private _client: HttpClient,
    private _messageService: MessageService,
    private _categories: string[],
    endpoint: string
  ) {
    this.endpoint = endpoint;
  }
  protected endpoint: string;

  get categories(): string[] {
    return this._categories;
  }

  getSelectionItem(
    id: string,
    state: { selectedItem: PropertyBaseSearchResultItem; options: any }
  ): Observable<PropertyBaseSearchResultItem> {
    // The detailed view is the same as the list view. No need to fetch again.
    if (state.selectedItem && state.selectedItem.value === id) return of(state.selectedItem);

    return this._client.get<PropertyBaseSearchResultItem>(`${this.endpoint}/${id}`);
  }

  getSelection(filter: string, options: any, page?: number): Observable<PropertyBaseSearchResultItem[]> {
    let queryArgs = {
      filter: filter,
      maxItems: 20,
    };
    if (options) queryArgs['options'] = this.getSelectionOptions(options);
    if (page && page > 0) queryArgs['page'] = page;

    return this._client.post<PropertyBaseSearchResultItem[]>(`${this.endpoint}/selection`, queryArgs).pipe(
      map(data => data.map(item => new PropertyBaseSearchResultItem(item))),
      catchError(e => {
        this._messageService.showError(e);
        throw e;
      })
    );
  }

  protected getSelectionOptions(options: any): any {
    return null;
  }
}

@Injectable()
export class PropertySearchService extends PropertyBaseSearchServiceBase {
  constructor(client: HttpClient, messageService: MessageService, @Inject(APP_CONFIG) config: IAppConfig) {
    super(client, messageService, ['property'], `${config.apiEndpointV2}/property-base-search/property`);
  }
}

@Injectable()
export class RentalObjectSearchService extends PropertyBaseSearchServiceBase {
  constructor(client: HttpClient, messageService: MessageService, @Inject(APP_CONFIG) config: IAppConfig) {
    super(
      client,
      messageService,
      ['dwelling', 'parking', 'otherRentalObject', 'premises', 'building', 'tenant'],
      `${config.apiEndpointV2}/property-base-search/rental-object`
    );
  }

  protected getSelectionOptions(options: any): any {
    if (options.categories) {
      return options.categories.filter(c => this.categories.find(cat => cat === c));
    }
  }
}
